import { useMutation, useQueryClient } from "react-query";
import { z } from "zod";

import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { getPuckPageQueryKey } from "./useGetPuckPage";
import { clearPersistedCache } from "src/_Globals/queryClient/cachePersistence";

export type PuckPage = {
    slug: string;
    title: string;
    content: string;
    locale: string;
    createdAt: string;
    updatedAt: string;
    url?: string;
    pageType?: "CMS" | "PDF";
    status?: "active" | "draft";
    allowedRoles?: string[] | null;
    sectors?: string[] | null;
};

export type CreatePuckPageRequest = {
    title: string;
    content: string;
    slug?: string;
    url?: string;
    pageType?: "CMS" | "PDF";
    status?: "active" | "draft";
    allowedRoles?: string[];
    sectors?: string[]; // IRIs
};

export const useCreatePuckPage = () => {
    const queryClient = useQueryClient();
    const URL = "/api/cms/puck/page";

    return useMutation<PuckPage, Error, CreatePuckPageRequest>({
        mutationFn: async (option: CreatePuckPageRequest) => {
            try {
                const validatedData = CreatePuckPageRequestSchema.parse(option);
                const response = await loggedInApiCall.post<PuckPage>(URL, validatedData);

                return PuckUpdateResponseSchema.parse(response.data);
            } catch (error) {
                if (error instanceof Error) {
                    throw error;
                }
                throw new Error("Failed to create Puck page: Unknown error", { cause: error });
            }
        },
        onSuccess: (data) => {
            clearPersistedCache();
            queryClient.invalidateQueries([getPuckPageQueryKey, data.slug]);
        },
    });
};

export const CreatePuckPageRequestSchema: z.ZodSchema<CreatePuckPageRequest> = z.object({
    title: z.string(),
    content: z.string(),
    slug: z.string().optional(),
    url: z.string().optional(),
    pageType: z.enum(["CMS", "PDF"]).optional(),
    status: z.enum(["active", "draft"]).optional(),
    allowedRoles: z.array(z.string()).optional(),
    sectors: z.array(z.string()).optional(),
});

export const PuckUpdateResponseSchema: z.ZodSchema<PuckPage> = z.object({
    slug: z.string(),
    title: z.string(),
    content: z.string(),
    locale: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    url: z.string().optional(),
    pageType: z.enum(["CMS", "PDF"]).optional(),
    status: z.enum(["active", "draft"]).optional(),
    allowedRoles: z.array(z.string()).nullish(),
    sectors: z.array(z.string()).nullish(),
});
